import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { SceneDefs } from '../assets/mine/SceneDefs.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { ErrorState, SplashScreen } from '../components/States.js';
import {
  preloadImages,
  registerArtworkCache,
  warmTier,
  type PreloadProgress,
} from '../services/preload.js';
import { reloadIfStale } from '../services/freshness.js';
import { loadGigapub } from '../services/gigapub.js';
import { useAdStore } from '../stores/ad.store.js';
import { ToastViewport } from '../components/Toast.js';
import { Notice } from '../components/Notice.js';
import { HomePage } from '../features/mining/HomePage.js';
import { PreviewPage } from '../features/dev/PreviewPage.js';
import { LevelUp } from '../components/LevelUp.js';
import { useLevelWatch } from '../hooks/useLevelWatch.js';
import { useErrorCopy } from '../lib/errorCopy.js';
import { ApiError, authenticate, onSuspended } from '../services/api.js';
import { BannedScreen } from '../features/ban/BannedScreen.js';
import { IpInUseScreen } from '../features/ban/IpInUseScreen.js';
import { ChannelGateScreen } from '../features/ban/ChannelGateScreen.js';
import { api, queryKeys } from '../services/endpoints.js';
import {
  backButton,
  getTelegramLanguage,
  initTelegram,
  observeViewport,
  setHapticsEnabled,
} from '../services/telegram.js';
import { I18nProvider, isLocale, preloadLocale, resolveLocale, useT } from '../i18n/index.js';
import { useMineAudio } from '../services/audio/useMineAudio.js';
import { usePresenceBeat } from '../services/presence.js';
import { rouletteAudio } from '../services/audio/roulette.js';
import { unlockSoundOnGesture } from '../services/audio/feedback.js';
import { BottomNav } from './BottomNav.js';
import {
  ActivityPage,
  ComboPage,
  MinersPage,
  MiningPage,
  preloadRoutes,
  ProfilePage,
  ReferralsPage,
  RoulettePage,
  TasksPage,
  WalletPage,
} from './routes.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Never retry a decision the server has already made.
        const status = (error as { status?: number })?.status;
        if (status !== undefined && status < 500 && status !== 429) return false;
        return failureCount < 2;
      },
      staleTime: 10_000,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

/**
 * How long the entry screen stays up after the sign-in and the artwork are both
 * in hand.
 *
 * The bar gives its first share to the real download and the rest to this
 * hold, so it keeps moving the whole time instead of sitting full, and it still
 * measures the whole wait.
 */
const SPLASH_HOLD_MS = 5_000;
/** The gold reaches the end a beat before the screen lifts, so the full bar is seen. */
const SPLASH_FILL_MS = SPLASH_HOLD_MS - 500;
/** The share of the bar the real download fills; the hold fills the rest. */
const SPLASH_DOWNLOAD_SHARE = 0.5;
/** How long after opening the rest of the artwork starts downloading, behind the main screens'. */
const LAZY_WARM_DELAY_MS = 6_000;
/** How long after opening GigaPub's script is fetched: behind the first screen, ahead of anyone reaching the task centre. */
const GIGAPUB_WARM_DELAY_MS = 3_000;

/**
 * GigaPub's script, a few seconds after the app opens (services/gigapub.ts).
 *
 * Its first act is to ask GigaPub's server which networks this project sells
 * for, and to load each one's SDK; an ad asked for before that is done can
 * only come from the networks already in hand, and GigaPub waits a few
 * seconds at most for the rest. Loaded on the tap, every first ad raced that;
 * loaded here, the list and the SDKs are ready by the time anyone reaches the
 * slot, as with the script in the page's head, where GigaPub's guide puts it.
 * Nothing is shown: the slot still asks for each ad. Off in the panel, or not
 * reached, it is simply loaded on the tap.
 */
async function warmGigapub(): Promise<void> {
  try {
    const slot = await queryClient.fetchQuery({ queryKey: queryKeys.gigapub, queryFn: api.ads.gigapub, staleTime: 30_000 });
    if (slot.enabled && slot.projectId) await loadGigapub(slot.projectId);
  } catch {
    // The slot loads it again when someone taps it.
  }
}

/**
 * Signs in and gets the artwork in hand before any screen renders.
 *
 * Every route needs an identity, and every screen is a painted picture, so both
 * are waited for here — at the same time, not one after the other: the sign-in
 * is one small request and the artwork is nine megabytes, and there is no
 * reason for either to hold up the other.
 *
 * A failed sign-in stops the app; a picture that will not load does not. See
 * services/preload.ts.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { t, locale } = useT();
  const describeError = useErrorCopy();
  const [status, setStatus] = useState<'pending' | 'ready' | 'failed'>('pending');
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [art, setArt] = useState<PreloadProgress>({ loaded: 0, total: 0, ratio: 0, done: false });
  // The hold (SPLASH_HOLD_MS): how far it has run, 0 to 1, and whether it is over.
  const [hold, setHold] = useState(0);
  const [lifted, setLifted] = useState(false);
  const inHand = status === 'ready' && art.done;

  // Suspended while the app is open: the next request finds out, and the whole
  // app gives way to the ban screen.
  useEffect(
    () =>
      onSuspended((suspension) => {
        setError(suspension);
        setStatus('failed');
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setStatus('pending');
    authenticate()
      .then(() => {
        if (!cancelled) setStatus('ready');
      })
      .catch((authError: unknown) => {
        if (cancelled) return;
        setError(authError);
        setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // Back from the background, the server may have moved on meanwhile: ask
  // again, but never during an ad, where a reload would lose it. And look at
  // the official channel afresh: someone who left it is sent to join again.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== 'visible' || useAdStore.getState().phase !== 'idle') return;
      void reloadIfStale();
      void api.channel.check().catch(() => undefined);
    };
    const telegram = (window as unknown as {
      Telegram?: { WebApp?: { onEvent?(name: string, handler: () => void): void; offEvent?(name: string, handler: () => void): void } };
    }).Telegram?.WebApp;
    document.addEventListener('visibilitychange', check);
    telegram?.onEvent?.('activated', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      telegram?.offEvent?.('activated', check);
    };
  }, []);

  // Once per load, not once per sign-in attempt: a retry re-authenticates, it
  // does not re-download the mine.
  useEffect(() => {
    let cancelled = false;
    // Before anything else: if this webview is running a bundle the server has
    // already replaced, it reloads itself rather than pretending to be current.
    void reloadIfStale();
    /*
     * The catalogue, alongside the artwork rather than after it.
     *
     * Every locale but English is a separate chunk, so without this the first
     * frame after the splash is in English and swaps a beat later. The splash
     * is already waiting, so there is nowhere cheaper to put this — and it is
     * not waited *on*: a catalogue that will not load must not hold the app
     * shut, and English is a working fallback.
     */
    void preloadLocale(locale);
    void preloadImages((progress) => {
      if (!cancelled) setArt(progress);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The hold starts the moment the sign-in and the artwork are both in hand.
  useEffect(() => {
    if (!inHand || lifted) return;
    const started = performance.now();
    const tick = window.setInterval(() => {
      setHold(Math.min(1, (performance.now() - started) / SPLASH_FILL_MS));
    }, 100);
    const lift = window.setTimeout(() => setLifted(true), SPLASH_HOLD_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(lift);
    };
  }, [inHand, lifted]);

  if (status === 'pending' || (status === 'ready' && !lifted)) {
    const progress = inHand
      ? SPLASH_DOWNLOAD_SHARE + hold * (1 - SPLASH_DOWNLOAD_SHARE)
      : art.total > 0
        ? art.ratio * SPLASH_DOWNLOAD_SHARE
        : undefined;
    return (
      <SplashScreen
        message={status === 'ready' ? t('boot.lighting') : t('boot.opening')}
        progress={progress}
        progressLabel={t('boot.artwork')}
      />
    );
  }

  if (status === 'failed') {
    // Suspended: the ban screen, with what the panel wrote — not a sign-in error.
    if (error instanceof ApiError && error.code === 'USER_SUSPENDED') {
      return <BannedScreen details={error.details} />;
    }
    // Another account holds this network: not a ban — a closed door until the
    // network changes or comes free.
    if (error instanceof ApiError && (error.code === 'IP_IN_USE' || error.code === 'IP_BANNED')) {
      return (
        <IpInUseScreen
          details={error.details}
          variant={error.code === 'IP_BANNED' ? 'banned' : 'in-use'}
          onRetry={() => setAttempt((value) => value + 1)}
        />
      );
    }
    // Not in the official channel: the way in is joining it.
    if (error instanceof ApiError && error.code === 'CHANNEL_REQUIRED') {
      return <ChannelGateScreen details={error.details} onRetry={() => setAttempt((value) => value + 1)} />;
    }
    const described = describeError(error);
    return (
      <div className="splash">
        <div style={{ width: 'min(100%, 380px)', padding: 'var(--space-5)' }}>
          <ErrorState
            title={described.title}
            description={described.description ?? t('boot.signInFailed')}
            onRetry={() => setAttempt((value) => value + 1)}
            retryLabel={t('common.retry')}
          />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * The work that is only worth doing once the app is actually open (§15, §16).
 *
 * This component renders nothing and mounts exactly when the splash comes
 * down, which is the point of it: everything here competes for the same
 * connection as the artwork the person is waiting on, so none of it may start
 * a moment earlier.
 *
 * In order of how much it matters:
 *
 *  1. **The artwork cache.** Registering the service worker is what makes the
 *     second visit free; installing it fetches the critical tier again, which
 *     is why it must not happen while the page is fetching that same tier.
 *  2. **The next tier.** The five screens the bottom bar leads to, fetched
 *     behind the first one, so the first tap paints instantly instead of
 *     building itself.
 *  3. **The route chunks**, last and during idle, because they are kilobytes
 *     against megabytes and arriving late costs nothing.
 */
function AfterBoot() {
  useEffect(() => {
    registerArtworkCache();
    void warmTier('high');
    preloadRoutes();
    // And then everything else — the roulette, the combo, the sheets — once the
    // five main screens have had the connection to themselves for a while, so
    // every picture in the app ends up in the cache before anyone asks for it.
    const rest = window.setTimeout(() => void warmTier('lazy'), LAZY_WARM_DELAY_MS);
    // GigaPub's networks, ready before anyone reaches the task centre.
    const gigapub = window.setTimeout(() => void warmGigapub(), GIGAPUB_WARM_DELAY_MS);
    return () => {
      window.clearTimeout(rest);
      window.clearTimeout(gigapub);
    };
  }, []);
  return null;
}

/**
 * Applies the stored preferences once a session exists. Haptics are the one
 * setting the client acts on by itself, so it is read here rather than only on
 * the Profile screen.
 */
function Preferences() {
  const { setLocale } = useT();
  const { data } = useQuery({ queryKey: queryKeys.profile, queryFn: api.profile.get, staleTime: 60_000 });

  /*
   * The mine's sound (§18).
   *
   * Read here rather than on the mine screen so it keeps playing while the
   * user is on Wallet or Tasks with a shift still running — the work does not
   * stop when you look away from it, and neither should the sound of it.
   *
   * `pendingClaimMpxc` is not the signal: a finished shift waiting to be
   * claimed is not a miner swinging. `activeMiners` is.
   */
  const { data: mine } = useQuery({
    queryKey: queryKeys.mine,
    queryFn: api.mine.get,
    staleTime: 10_000,
  });

  // A level reached anywhere — a claim, a task, the wheel, a friend's share —
  // comes back on the mine or the profile; the jump plays from there.
  useLevelWatch(data?.user, mine?.user);

  useMineAudio({
    enabled: data?.settings.sound ?? true,
    volume: (data?.settings.volume ?? 70) / 100,
    working: (mine?.summary.activeMiners ?? 0) > 0,
  });
  useEffect(() => {
    if (data) setHapticsEnabled(data.settings.haptics);
  }, [data]);

  /*
   * The success and error sounds (services/audio/feedback.ts) are the wheel's
   * own, so they play at the volume set in Profile, as the wheel does; nought
   * silences them. Every tap wakes the engine, so a reward that comes back from
   * the server later can still be heard.
   */
  useEffect(() => {
    rouletteAudio.setVolume((data?.settings.volume ?? 70) / 100);
  }, [data?.settings.volume]);
  useEffect(() => unlockSoundOnGesture(), []);

  /*
   * The account is the authority on language.
   *
   * The first paint uses what Telegram reported and what the last session
   * stored; this is what makes the choice follow the account to a new device.
   * A stored value for a language this build does not carry is ignored rather
   * than applied, so removing a locale cannot leave someone stuck.
   */
  useEffect(() => {
    const language = data?.settings.language;
    if (isLocale(language)) setLocale(language);
  }, [data?.settings.language, setLocale]);

  return null;
}

/** Counts this phone in the admin panel's "online now" while the app is open (services/presence). Shows nothing. */
function Presence() {
  usePresenceBeat();
  return null;
}

/**
 * The route slot, held open while a screen's chunk arrives (§17).
 *
 * The fallback is deliberately nothing at all. Every screen here is split, so
 * React needs a boundary; but the chunks are already fetched by the time
 * anyone navigates (see routes.ts), so what this actually guards is the rare
 * case — a cold cache, a dropped connection mid-session — and in that case a
 * spinner that appears for two frames and vanishes is worse than a beat of the
 * frame the person is already looking at. The shell, the bottom bar and the
 * toasts sit outside the boundary and never unmount, so "nothing" means the
 * app, not a blank screen.
 */
function Screen() {
  return (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
}

/** The app frame: scrollable route area and bottom navigation. */
function Shell() {
  const location = useLocation();
  const navigate = useNavigate();

  // Telegram's own back button drives navigation everywhere except Home.
  useEffect(() => {
    if (location.pathname === '/') {
      backButton.hide();
      return;
    }
    return backButton.show(() => navigate(-1));
  }, [location.pathname, navigate]);

  // Each route change starts at the top; a mid-scroll arrival reads as a bug.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <div className="app">
      {/*
        No app header. The Mini App runs fullscreen in Telegram, whose own
        close and menu buttons float over the top of the screen, so every screen
        starts with its own content, padded clear of them by --safe-top. The
        balance lives on Home and Wallet, and Profile and settings are in the
        bottom nav.
      */}
      <main className="app__main">
        <Screen />
      </main>
      <BottomNav />
      <ToastViewport />
      <Notice />
    </div>
  );
}

/**
 * The frame for immersive routes (§9, §19): no header, no bottom navigation, no
 * page padding. Telegram's own back button is the way out, which is what makes
 * it feel like a Mini App instead of a website in a webview.
 */
function FullscreenRoute() {
  const navigate = useNavigate();

  useEffect(() => {
    return backButton.show(() => navigate('/'));
  }, [navigate]);

  useEffect(() => {
    document.body.classList.add('is-immersive');
    return () => document.body.classList.remove('is-immersive');
  }, []);

  return (
    <div className="app app--immersive">
      <Screen />
      <ToastViewport />
      <Notice />
    </div>
  );
}

/**
 * The frame for the painted pages that take the whole screen but still scroll —
 * the daily combo, the roulette and the crew list: no bottom navigation,
 * Telegram's own back button (and each page's own way out) the way back —
 * to Home, or for the crew to the mine it belongs to (`back`).
 */
function SceneRoute({ back = '/' }: { back?: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    return backButton.show(() => navigate(back));
  }, [navigate, back]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <div className="app app--scene">
      <main className="app__main app__main--scene">
        <Screen />
      </main>
      <ToastViewport />
      <Notice />
    </div>
  );
}

function NotFound() {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <div className="page">
      <ErrorState
        title={t('boot.notFoundTitle')}
        description={t('boot.notFoundBody')}
        onRetry={() => navigate('/')}
        retryLabel={t('boot.notFoundCta')}
      />
    </div>
  );
}

export function App() {
  useEffect(() => {
    initTelegram();
    // Keep the layout honest when Telegram resizes its viewport.
    return observeViewport((height) => {
      document.documentElement.style.setProperty('--tg-viewport-height', `${height}px`);
    });
  }, []);

  return (
    /*
      The translator is the outermost provider, above the error boundary, so
      the boundary's own fallback can be translated: a crash screen in a
      language the reader does not have is barely better than a blank one.
      Nothing in the provider can throw, so it costs the boundary no coverage.
    */
    <I18nProvider initialLocale={resolveLocale(getTelegramLanguage())}>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <SceneDefs />
      <AuthGate>
        <AfterBoot />
        <Preferences />
        <Presence />
        <LevelUp />
        <BrowserRouter>
          <Routes>
            <Route path="/mining" element={<FullscreenRoute />}>
              <Route index element={<MiningPage />} />
            </Route>
            {/* A design measuring tool: development builds only, never shipped. */}
            {import.meta.env.DEV ? <Route path="/preview" element={<PreviewPage />} /> : null}
            <Route element={<SceneRoute />}>
              <Route path="/combo" element={<ComboPage />} />
              <Route path="/roulette" element={<RoulettePage />} />
            </Route>
            {/* The crew is part of the mine: like the mine, no navigation bar. */}
            <Route element={<SceneRoute back="/mining" />}>
              <Route path="/miners" element={<MinersPage />} />
            </Route>
            <Route element={<Shell />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/invite" element={<ReferralsPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthGate>
      </QueryClientProvider>
    </ErrorBoundary>
    </I18nProvider>
  );
}
